import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect:
      typeof search.redirect === "string" && search.redirect.startsWith("/")
        ? search.redirect
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Sign In — KAKAIYS" },
      { name: "description", content: "Sign in to KAKAIYS with your Google account." },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const { redirect = "/" } = Route.useSearch();
  const { user, loading } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  // If already signed in (e.g., returning from Google OAuth), forward to redirect.
  useEffect(() => {
    if (!loading && user) {
      navigate({ to: redirect, replace: true });
    }
  }, [loading, user, redirect, navigate]);

  const handleGoogle = async () => {
    setSubmitting(true);
    // Native Supabase OAuth — no Lovable wrapper. Works identically on any host.
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}${redirect}`,
      },
    });

    if (error) {
      toast.error(error.message ?? "Sign in failed.");
      setSubmitting(false);
      return;
    }
    // Browser is redirecting to Google; will return to redirectTo URL.
  };

  return (
    <section className="pt-32 md:pt-40 pb-24 container-luxe">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-12">
          <div className="eyebrow mb-3">Welcome</div>
          <h1 className="font-serif text-4xl md:text-5xl">Sign in.</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Continue with Google to shop the maison. No separate account required.
          </p>
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={submitting}
          className="w-full flex items-center justify-center gap-3 bg-foreground text-background py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-foreground/90 disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M21.35 11.1H12v3.2h5.35c-.23 1.4-1.66 4.1-5.35 4.1-3.22 0-5.85-2.66-5.85-5.95S8.78 6.5 12 6.5c1.83 0 3.06.78 3.76 1.45l2.57-2.48C16.78 3.95 14.6 3 12 3 6.98 3 3 6.98 3 12s3.98 9 9 9c5.2 0 8.64-3.66 8.64-8.8 0-.6-.06-1.06-.14-1.5z"
            />
          </svg>
          {submitting ? "Redirecting…" : "Continue with Google"}
        </button>

        <div className="mt-10 text-center">
          <Link
            to="/"
            className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground"
          >
            ← Back to maison
          </Link>
        </div>
      </div>
    </section>
  );
}
